import { useEffect, useState, useSyncExternalStore } from "react";

function subscribeVisibility(onChange: () => void) {
  document.addEventListener("visibilitychange", onChange);
  return () => document.removeEventListener("visibilitychange", onChange);
}

const pageVisible = () => document.visibilityState === "visible";

/**
 * Whether a card is worth keeping live: on screen (IntersectionObserver) and in a tab the user is
 * looking at. Pass the returned ref to the card's root. Without IntersectionObserver the card
 * counts as on screen.
 */
export function useLiveWhileVisible<T extends Element>(): [(node: T | null) => void, boolean] {
  const [node, setNode] = useState<T | null>(null);
  const [onScreen, setOnScreen] = useState(false);
  const visible = useSyncExternalStore(subscribeVisibility, pageVisible, () => false);

  useEffect(() => {
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setOnScreen(true);
      return;
    }
    // One target: a batch holds its changes in order, so the last entry is where it is now.
    const observer = new IntersectionObserver((entries) => {
      setOnScreen(entries.at(-1)?.isIntersecting ?? false);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return [setNode, onScreen && visible];
}
